import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Typography, Box, useMediaQuery, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { typeByName } from '../../../constants/Filters';
import { actions } from '../../../actions';
import InputFilter from './InputFilter';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import moment from 'moment';
import { isNull, isUndefined } from 'lodash';
import esLocale from 'date-fns/locale/es';
import ClientSelector from '../../../shared/ClientSelector/ClientSelector';
import propTypes from 'prop-types';

const useStyles = (small) => {
  return makeStyles((theme) => ({
    appBarSpacer: !small && theme.mixins.toolbar,
    chatsFilter: {
      boxShadow: small ? 'none' : '0 .125rem .25rem rgba(0,0,0,.075)!important',
      width: small ? '300px' : '100%',
      borderBottom: '1px solid #eee',
      userSelect: 'none',
    },
    chips: {
      textAlign: 'center',
      borderRadius: '3rem',
      cursor: 'pointer',
      paddingInline: 8,
      transition: 'all .2s',
      '&:hover': {
        transform: 'scale(1.1)',
      },
      fontSize: theme.sizes.sm.t2,
      minWidth: 'max-content',
    },
    chip: {
      width: 'fit-content',
    },
    chipState: {
      margin: small && '4px 0',
      padding: '4px 4px',
    },
    chipsActive: {
      background: theme.palette.primary.main,
      '&:hover': {
        background: theme.palette.primary.light,
      },
      color: theme.palette.white.main,
    },
    keywordsField: {
      width: small && '100%',
    },
    statusCount: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '15px',
      width: 'fit-content',
      height: '15px',
      fontSize: 10,
      marginLeft: 4,
      backgroundColor: theme.palette.white.main,
      color: theme.palette.black.main,
      paddingInline: 2,
      borderRadius: '3rem',
    },
    avatarChip: {
      minWidth: 'fit-content',
    },
    close: {
      cursor: 'pointer',
      '&:hover': {
        opacity: '.7',
      },
      right: 0,
      bottom: 'calc(50% - 15px)',
    },
    search: {
      zIndex: 3,
      position: 'absolute',
      right: 32,
      cursor: 'pointer',
      top: 86,
      background: '#eee',
      padding: 4,
      borderRadius: '3rem',
    },
    searchIcon: {
      width: 30,
      height: 30,
      '&:hover': {
        opacity: '.7',
      },
    },
    filterChips: {
      margin: !small ? '4px 16px' : '',
      maxWidth: !small ? '65%' : '100%',
      marginBottom: 4,
    },
  }));
};

function ChatsFilter(props) {
  const { closeMenu } = props;
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();
  const appDispatch = useDispatch();
  const [date, setDate] = useState(Date.now());
  const organization = useSelector((state) => state.app.organization);
  const config = useSelector((state) => state.app.prestobotsConfig);
  const openFilters = useSelector((state) => state.app.openSection.filter);
  const filter = useSelector((state) => state.app.filter);
  const countChats = useSelector((state) => state.app.countChats);
  const showChatsNoEnded = useSelector((state) => state.app.activeChatShowChatsNoEnded);
  const profile = useSelector((state) => state.firebase.profile);

  function handleOnClickSetStatus(value) {
    // if state is same as before, deselect it
    const status = value === filter.local.status ? -1 : value;

    appDispatch({ type: actions.filterStatus, status });
  }

  function handleOnClickSetType(value) {
    // if state is same as before, deselect it
    const typeValue = value === filter.local.type ? -1 : value;

    appDispatch({ type: actions.filterType, typeValue });
  }

  function handleOnClickSetBotStopped(value) {
    const botStopped = value === filter.local.botStopped ? -1 : value;
    appDispatch({ type: actions.filterBotStopped, botStopped });
  }

  function handleOnClickSetMyChats(value) {
    const myChats = value === filter.local.myChats ? -1 : value;

    appDispatch({ type: actions.filterMyChats, myChats });
  }

  const handleDateChange = (_date) => {
    const date = moment(new Date(_date));
    setDate(date);
    appDispatch({ type: actions.filterDate, date });
  };

  const notificationsService =
    organization && organization.services && organization.services.notifications;

  const states = useMemo(() => {
    if (!organization || !organization.states || !profile) return [];
    const { configuration } = organization;
    const chatStatesConfig = configuration?.chatStates?.userAssociatedStates;
    const associatedStates = chatStatesConfig?.associatedStates || [];
    const isRestricted = chatStatesConfig?.enabled;
    const profileHasClaims = profile?.claims && profile.claims.length > 0;
    const organizationStatesIncludingTerminated = [...organization.states, 3];
    if (profileHasClaims)
      return [...organizationStatesIncludingTerminated, notificationsService && 6];

    if (!isRestricted) return [...organizationStatesIncludingTerminated, notificationsService && 6];

    return [
      ...organizationStatesIncludingTerminated.filter((state) => {
        const associatedState = associatedStates.find(
          ({ state: associatedState }) => associatedState.key === state,
        );
        if (associatedState) {
          return associatedState.users.some((user) => user.email === profile.email);
        }
        return true;
      }),
      notificationsService && 6,
    ];
  }, [organization]);

  return (
    <Box className={classes.chatsFilter} px={1}>
      <div className={classes.appBarSpacer} />
      <Box
        container
        sx={{ display: 'flex', position: 'relative', flexDirection: small ? 'column' : '' }}
      >
        <Grid className={classes.filterChips}>
          <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
            <Grid style={{ display: !openFilters && 'none' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                Filtrar chats por estados
              </Typography>
            </Grid>
            <Grid
              container
              className="scroll"
              direction="row"
              sx={{
                display: 'flex',
                padding: !openFilters && '2px 0',
                flexWrap: 'wrap',
                paddingBottom: 1,
                overflowX: 'auto',
              }}
            >
              {states
                .filter(
                  (state) =>
                    !isNull(state) &&
                    !isUndefined(state) &&
                    config &&
                    config.states &&
                    config.states.find((e) => e.key === state),
                )
                .map(
                  (state) => config && config.states && config.states.find((e) => e.key === state),
                )
                .map((state) => (
                  <Box key={state.key} item className={classes.chipState}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      className={classes.chips}
                      sx={{
                        backgroundColor: filter.local.status.includes(state.key)
                          ? state.color || '#ffcc01'
                          : 'gray.main',
                        color: filter.local.status.includes(state.key)
                          ? 'white !important'
                          : 'black',
                      }}
                      onClick={() => handleOnClickSetStatus(state.key)}
                    >
                      {state.label}
                      <Box className={classes.statusCount}>
                        {countChats.byStatus[state.key] || 0}
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Grid>
          </Grid>
          <Grid container direction="row" style={{ display: !openFilters && 'none' }}>
            <Grid
              container
              sx={{ display: 'flex', flexDirection: small ? 'column' : '', flexWrap: 'nowrap' }}
            >
              <Grid container sx={{ minWidth: 'max-content' }}>
                <Grid>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Tipo de chat
                  </Typography>
                </Grid>
                <Grid container direction="row" sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <Box item className={classes.chipState}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      className={`${classes.chips} ${
                        filter.local.type === typeByName.web && classes.chipsActive
                      }`}
                      sx={{ backgroundColor: 'gray.main' }}
                      onClick={() => handleOnClickSetType(typeByName.web)}
                    >
                      Webchat
                      <Box className={classes.statusCount}>{countChats.byType[typeByName.web]}</Box>
                    </Box>
                  </Box>
                  <Box item className={classes.chipState}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      className={`${classes.chips} ${
                        filter.local.type === typeByName.whatsapp && classes.chipsActive
                      }`}
                      sx={{ backgroundColor: 'gray.main' }}
                      onClick={() => handleOnClickSetType(typeByName.whatsapp)}
                    >
                      WhatsApp
                      <Box className={classes.statusCount}>
                        {countChats.byType[typeByName.whatsapp]}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid container>
                <Grid>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Otros
                  </Typography>
                </Grid>
                <Grid container sx={{ flexWrap: 'nowrap' }}>
                  <Box item className={classes.chipState}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      className={`${classes.chips} ${
                        filter.local.botStopped === true && classes.chipsActive
                      }`}
                      sx={{ backgroundColor: 'gray.main' }}
                      onClick={() => handleOnClickSetBotStopped(true)}
                    >
                      Bot Detenido
                      <Box className={classes.statusCount}>{countChats.botStopped}</Box>
                    </Box>
                  </Box>
                  {organization &&
                    organization.businessRules &&
                    organization.businessRules.misChats && (
                      <Box item className={classes.chipState}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          className={`${classes.chips} ${
                            filter.local.myChats === true && classes.chipsActive
                          }`}
                          sx={{ backgroundColor: 'gray.main' }}
                          onClick={() => handleOnClickSetMyChats(true)}
                        >
                          Mis chats
                          <Box className={classes.statusCount}>{countChats.myChats}</Box>
                        </Box>
                      </Box>
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid style={{ display: !openFilters && 'none' }}>
          <Grid container>
            <Box
              flex={1}
              display="flex"
              flexDirection={small ? 'column' : 'row'}
              alignItems="center"
              justifyContent="flex-start"
              pb={4}
            >
              <Grid item sm={5} sx={{ pt: 1 }}>
                <LocalizationProvider adapterLocale={esLocale} dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Buscar por fecha"
                    inputFormat="dd/MM/yyyy"
                    value={date}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} />}
                    disableFuture
                    disabled={showChatsNoEnded}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Box ml={1}>
                  <InputFilter />
                </Box>
              </Grid>

              {small && (
                <Grid item xs={12} sm={5}>
                  <Box ml={1}>
                    <ClientSelector closeMenu={closeMenu} color="black" isTransparent={false} />
                  </Box>
                </Grid>
              )}

              {!small && (
                <Box
                  className={classes.close}
                  position="absolute"
                  onClick={() => {
                    appDispatch({ type: actions.openSection, openSection: { filter: false } });
                  }}
                >
                  <ArrowDropUp />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
        {!small && !openFilters && (
          <Box
            className={classes.close}
            position="absolute"
            onClick={() => {
              appDispatch({ type: actions.openSection, openSection: { filter: true } });
            }}
          >
            <ArrowDropDown />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const Props = {
  closeMenu: propTypes.func.isRequired,
};

ChatsFilter.propTypes = Props;

export default ChatsFilter;
